<script setup lang="ts">
/**
 * AI Model Selector (header)
 * Project-scoped dropdown to choose the AI model for all AI operations.
 * Groups models by provider, with inline API key input for BYOK providers.
 * Only visible when a project is active. Reads/writes to project AI settings.
 */
import { ref } from 'vue'
import {
  Bot, ChevronDown, Loader2, Check, Zap, Cpu, Diamond,
  AlertTriangle, Key, ExternalLink, Eye, EyeOff, X, ShieldCheck,
} from 'lucide-vue-next'
import { api } from '@/core/api'
import { useClickOutside } from '@/composables/useClickOutside'
import { useProjectAiModelPicker } from '@/composables/useProjectAiModelPicker'
import { logger } from '@/utils/logger'
import { useSettingsStore, type AiModelInfo } from '@/stores/settings'

const settingsStore = useSettingsStore()
const isOpen = ref(false)
const wrapperRef = ref<HTMLElement | null>(null)

const {
  loading,
  loadFailed,
  selectedModel,
  orderedProviders,
  isModelSelectable,
  loadModels,
  selectModel: persistModel,
} = useProjectAiModelPicker()

useClickOutside(wrapperRef, () => { isOpen.value = false })

// --- API key state per provider ---
const apiKeyInputs = ref<Record<string, string>>({})
const apiKeyExpanded = ref<Record<string, boolean>>({})
const apiKeyValidating = ref<Record<string, boolean>>({})
const apiKeyValidationResult = ref<Record<string, 'valid' | 'invalid' | null>>({})
const apiKeySaving = ref<Record<string, boolean>>({})
const apiKeyShowPlain = ref<Record<string, boolean>>({})

function categoryIcon(cat: string) {
  if (cat === 'fast') return Zap
  if (cat === 'flagship') return Diamond
  return Cpu
}

function categoryGradient(cat: string): string {
  if (cat === 'fast') return 'bg-gradient-to-br from-emerald-500 to-emerald-600'
  if (cat === 'flagship') return 'bg-gradient-to-br from-amber-500 to-orange-500'
  return 'bg-gradient-to-br from-brand to-accent-purple'
}

function providerGradient(providerType: string): string {
  switch (providerType) {
    case 'claude': return 'bg-gradient-to-br from-orange-500 to-orange-600'
    case 'gemini': return 'bg-gradient-to-br from-sky-500 to-indigo-500'
    default: return 'bg-gradient-to-br from-brand to-accent-purple'
  }
}

async function selectModel(model: AiModelInfo) {
  if (!isModelSelectable(model)) return
  isOpen.value = false
  await persistModel(model)
}

function toggleApiKeySection(providerType: string) {
  apiKeyExpanded.value = {
    ...apiKeyExpanded.value,
    [providerType]: !apiKeyExpanded.value[providerType],
  }
}

function toggleKeyVisibility(providerType: string) {
  apiKeyShowPlain.value = {
    ...apiKeyShowPlain.value,
    [providerType]: !apiKeyShowPlain.value[providerType],
  }
}

async function saveApiKey(providerType: string) {
  const key = apiKeyInputs.value[providerType]?.trim()
  if (!key) return

  apiKeyValidating.value = { ...apiKeyValidating.value, [providerType]: true }
  apiKeyValidationResult.value = { ...apiKeyValidationResult.value, [providerType]: null }
  try {
    const result = await api.validateProviderApiKey(providerType, key)
    if (!result?.valid) {
      apiKeyValidationResult.value = { ...apiKeyValidationResult.value, [providerType]: 'invalid' }
      return
    }
    apiKeyValidationResult.value = { ...apiKeyValidationResult.value, [providerType]: 'valid' }
  } catch {
    apiKeyValidationResult.value = { ...apiKeyValidationResult.value, [providerType]: 'invalid' }
    return
  } finally {
    apiKeyValidating.value = { ...apiKeyValidating.value, [providerType]: false }
  }

  apiKeySaving.value = { ...apiKeySaving.value, [providerType]: true }
  try {
    const updatedKeys = { ...settingsStore.customApiKeys, [providerType]: key }
    await api.updateAISettings({
      customApiKeys: updatedKeys,
      useOwnApiKey: true,
    })
    settingsStore.setCustomApiKey(providerType, key)
    apiKeyInputs.value = { ...apiKeyInputs.value, [providerType]: '' }
  } catch (err) {
    logger.error('AiModelSelector', `Failed to save API key for ${providerType}:`, err)
  } finally {
    apiKeySaving.value = { ...apiKeySaving.value, [providerType]: false }
  }
}

async function removeApiKey(providerType: string) {
  try {
    const updatedKeys = { ...settingsStore.customApiKeys }
    delete updatedKeys[providerType]
    const hasAnyKey = Object.values(updatedKeys).some(k => !!k)
    await api.updateAISettings({
      customApiKeys: updatedKeys,
      useOwnApiKey: hasAnyKey,
    })
    settingsStore.removeCustomApiKey(providerType)
    apiKeyValidationResult.value = { ...apiKeyValidationResult.value, [providerType]: null }
    apiKeyExpanded.value = { ...apiKeyExpanded.value, [providerType]: false }
  } catch (err) {
    logger.error('AiModelSelector', `Failed to remove API key for ${providerType}:`, err)
  }
}
</script>

<template>
  <div ref="wrapperRef" class="relative flex items-center">
    <!-- Trigger button -->
    <button
      class="flex items-center gap-2 py-1.5 px-3 bg-surface-tertiary border border-border rounded-md cursor-pointer transition-all duration-200 text-[13px] text-foreground min-w-[180px] justify-between hover:bg-surface-hover hover:border-border-hover max-md:min-w-0 max-md:px-2.5"
      @click.stop="isOpen = !isOpen"
    >
      <div :class="['flex items-center justify-center w-5 h-5 rounded shrink-0', selectedModel ? providerGradient(selectedModel.provider) : 'bg-gradient-to-br from-brand to-accent-purple']">
        <Bot :size="12" color="white" />
      </div>
      <div class="flex flex-col items-start flex-1 overflow-hidden max-md:hidden">
        <span class="text-[9px] font-semibold uppercase text-foreground-muted tracking-wide">AI Model</span>
        <span class="text-xs font-medium text-foreground whitespace-nowrap overflow-hidden text-ellipsis max-w-[140px]">
          {{ loadFailed ? 'Unavailable' : selectedModel?.name || 'Loading...' }}
        </span>
      </div>
      <ChevronDown
        :size="14"
        :class="['text-foreground-muted shrink-0 transition-transform duration-200 max-md:hidden', isOpen && 'rotate-180']"
      />
    </button>

    <!-- Dropdown panel -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2.5"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2.5"
    >
      <div
        v-if="isOpen"
        class="absolute top-[calc(100%+8px)] right-0 max-md:right-[-50px] min-w-[360px] max-md:min-w-[300px] bg-surface border border-border rounded-xl shadow-lg z-[1000] max-h-[480px] overflow-y-auto"
      >
        <!-- Loading state -->
        <div v-if="loading" class="flex items-center justify-center p-5 text-foreground-muted text-[13px] gap-2">
          <Loader2 :size="14" class="animate-spin" />
          Loading models...
        </div>

        <!-- Error state -->
        <div v-else-if="loadFailed || orderedProviders.length === 0" class="flex flex-col items-center gap-2 p-5 text-center">
          <AlertTriangle :size="20" class="text-yellow-500" />
          <span class="text-[13px] text-foreground-muted">
            Failed to load AI models. Check API key configuration.
          </span>
          <button
            class="text-[12px] text-brand hover:underline cursor-pointer mt-1"
            @click="loadModels()"
          >
            Retry
          </button>
        </div>

        <!-- Provider groups -->
        <template v-else>
          <div
            v-for="(group, groupIdx) in orderedProviders"
            :key="group.meta.type"
          >
            <!-- Provider header -->
            <div
              :class="['flex items-center gap-2 px-4 py-2 bg-surface-tertiary border-y border-border/40', groupIdx === 0 ? 'border-t-0' : 'mt-2']"
            >
              <div :class="['flex items-center justify-center w-5 h-5 rounded shrink-0', providerGradient(group.meta.type)]">
                <Bot :size="11" color="white" />
              </div>
              <span class="text-[11px] font-bold text-foreground uppercase tracking-wider flex-1">
                {{ group.meta.displayName }}
              </span>

              <div class="flex items-center gap-3">
                <span
                  v-if="group.meta.requiresApiKey && !settingsStore.hasApiKey(group.meta.type)"
                  class="flex items-center gap-1 text-[10px] font-medium text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded"
                >
                  <AlertTriangle :size="10" />
                  Key required
                </span>

                <button
                  v-if="!settingsStore.hasApiKey(group.meta.type)"
                  class="flex items-center gap-1 text-[11px] text-foreground-muted hover:text-brand transition-colors cursor-pointer"
                  @click.stop="toggleApiKeySection(group.meta.type)"
                >
                  <Key :size="10" />
                  Use own API key
                </button>

                <div
                  v-else-if="settingsStore.hasApiKey(group.meta.type)"
                  class="flex items-center gap-1 text-[11px]"
                >
                  <ShieldCheck :size="12" class="text-emerald-500" />
                  <span class="text-[11px] text-emerald-600 dark:text-emerald-400">
                    Your key
                  </span>
                  <button
                    class="ml-1 text-[11px] text-foreground-muted hover:text-red-500 transition-colors cursor-pointer flex items-center"
                    @click.stop="removeApiKey(group.meta.type)"
                    title="Remove API Key"
                  >
                    <X :size="12" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Models list -->
            <div
              v-for="model in group.models"
              :key="model.id"
              :class="[
                'flex items-center gap-3 px-4 py-2 transition-colors duration-150',
                !isModelSelectable(model) ? 'opacity-40 cursor-not-allowed' :
                model.id === settingsStore.selectedModel ? 'bg-brand/10 cursor-pointer' : 'hover:bg-surface-tertiary cursor-pointer',
              ]"
              @click="selectModel(model)"
            >
              <div :class="['flex items-center justify-center w-7 h-7 rounded-md shrink-0', categoryGradient(model.category)]">
                <component :is="categoryIcon(model.category)" :size="14" color="white" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-[13px] font-medium text-foreground">
                  {{ model.name }}
                </div>
                <div class="text-[11px] text-foreground-muted mt-0.5">
                  {{ model.description }}
                </div>
              </div>
              <Check
                v-if="isModelSelectable(model)"
                :size="16"
                :class="['text-brand shrink-0 transition-opacity', model.id === settingsStore.selectedModel ? 'opacity-100' : 'opacity-0']"
              />
              <Key
                v-else
                :size="14"
                class="text-foreground-muted shrink-0"
              />
            </div>

            <!-- API key section -->
            <div class="px-4 pb-2" v-if="apiKeyExpanded[group.meta.type] && !settingsStore.hasApiKey(group.meta.type)">
              <div class="flex flex-col gap-1.5 mt-1 p-2.5 bg-surface-tertiary rounded-lg border border-border">
                <div class="flex items-center justify-between mb-0.5">
                  <span class="text-[11px] text-foreground-muted font-medium">Enter your API key</span>
                  <button
                    class="text-foreground-muted hover:text-foreground transition-colors p-0.5"
                    @click.stop="toggleApiKeySection(group.meta.type)"
                  >
                    <X :size="12" />
                  </button>
                </div>
                <div class="flex items-center gap-1.5">
                  <div class="relative flex-1">
                    <input
                      :type="apiKeyShowPlain[group.meta.type] ? 'text' : 'password'"
                      :placeholder="group.meta.apiKeyPlaceholder"
                      :value="apiKeyInputs[group.meta.type] || ''"
                      class="w-full text-[12px] bg-surface border border-border rounded-md px-2.5 py-1.5 pr-7 text-foreground placeholder-foreground-muted/50 focus:outline-none focus:border-brand/50 focus:ring-1 focus:ring-brand/20 transition-colors"
                      @input="(e) => { apiKeyInputs = { ...apiKeyInputs, [group.meta.type]: (e.target as HTMLInputElement).value }; apiKeyValidationResult = { ...apiKeyValidationResult, [group.meta.type]: null } }"
                      @keydown.enter="saveApiKey(group.meta.type)"
                    />
                    <button
                      class="absolute right-1.5 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground transition-colors p-0.5"
                      @click.stop="toggleKeyVisibility(group.meta.type)"
                    >
                      <component :is="apiKeyShowPlain[group.meta.type] ? EyeOff : Eye" :size="12" />
                    </button>
                  </div>
                  <button
                    class="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium bg-brand text-white rounded-md hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    :disabled="!apiKeyInputs[group.meta.type]?.trim() || apiKeyValidating[group.meta.type] || apiKeySaving[group.meta.type]"
                    @click.stop="saveApiKey(group.meta.type)"
                  >
                    <Loader2 v-if="apiKeyValidating[group.meta.type] || apiKeySaving[group.meta.type]" :size="11" class="animate-spin" />
                    <template v-else>Save</template>
                  </button>
                </div>
                <div v-if="apiKeyValidationResult[group.meta.type] === 'invalid'" class="text-[11px] text-red-500 flex items-center gap-1">
                  <X :size="10" />
                  Invalid API key. Please check and try again.
                </div>
                <a
                  :href="group.meta.apiKeyHelpUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-[11px] text-brand hover:underline flex items-center gap-1"
                  @click.stop
                >
                  Get your API key
                  <ExternalLink :size="10" />
                </a>
              </div>
            </div>
          </div>
        </template>
      </div>
    </Transition>
  </div>
</template>
